import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { gdprService, profileService, type GdprData } from '@/services';

export default function GDPRChecklist() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [gdprId, setGdprId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    mainContact: '',
    dpoName: '',
    dpoEmail: '',
    euRepName: '',
    euRepEmail: '',
    euRepPhone: '',
    euRepAddress1: '',
    euRepAddress2: '',
    euRepAddress3: '',
    euRepCity: '',
    euRepState: '',
    euRepCountry: '',
  });

  const [taskComplete, setTaskComplete] = useState({
    mainContact: false,
    dpo: false,
    euRep: false,
    privacyPolicy: false,
    nda: false,
    contractClauses: false,
  });

  useEffect(() => {
    const fetchGDPRData = async () => {
      if (!user) return;

      try {
        // Get user's company_id
        const profileResult = await profileService.get(user.id);
        if (profileResult.error) throw profileResult.error;

        if (!profileResult.data?.company_id) {
          setIsFetching(false);
          return;
        }

        setCompanyId(profileResult.data.company_id);

        // Fetch existing GDPR record using service
        const result = await gdprService.getByCompanyId(profileResult.data.company_id);
        
        if (result.error) throw result.error;

        const gdpr = result.data;
        if (gdpr) {
          setGdprId(gdpr.id);
          setFormData({
            mainContact: gdpr.main_contact || '',
            dpoName: gdpr.dpo_name || '',
            dpoEmail: gdpr.dpo_email || '',
            euRepName: gdpr.eu_rep_name || '',
            euRepEmail: gdpr.eu_rep_email || '',
            euRepPhone: gdpr.eu_rep_phone || '',
            euRepAddress1: gdpr.eu_rep_address1 || '',
            euRepAddress2: gdpr.eu_rep_address2 || '',
            euRepAddress3: gdpr.eu_rep_address3 || '',
            euRepCity: gdpr.eu_rep_city || '',
            euRepState: gdpr.eu_rep_state || '',
            euRepCountry: gdpr.eu_rep_country || '',
          });
          setTaskComplete({
            mainContact: gdpr.task_main_contact_complete || false,
            dpo: gdpr.task_dpo_complete || false,
            euRep: gdpr.task_eu_rep_complete || false,
            privacyPolicy: gdpr.task_privacy_policy_complete || false,
            nda: gdpr.task_nda_complete || false,
            contractClauses: gdpr.task_contract_clauses_complete || false,
          });
        }
      } catch (error) {
        console.error('Error fetching GDPR data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load GDPR data.',
          variant: 'destructive',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchGDPRData();
  }, [user, toast]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskToggle = (task: string) => {
    setTaskComplete(prev => ({ ...prev, [task]: !prev[task as keyof typeof prev] }));
  };

  const handleSave = async () => {
    if (!companyId || !user?.id) {
      toast({
        title: 'Error',
        description: 'No company associated with your profile.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const gdprData: GdprData = {
        main_contact: formData.mainContact || null,
        dpo_name: formData.dpoName || null,
        dpo_email: formData.dpoEmail || null,
        eu_rep_name: formData.euRepName || null,
        eu_rep_email: formData.euRepEmail || null,
        eu_rep_phone: formData.euRepPhone || null,
        eu_rep_address1: formData.euRepAddress1 || null,
        eu_rep_address2: formData.euRepAddress2 || null,
        eu_rep_address3: formData.euRepAddress3 || null,
        eu_rep_city: formData.euRepCity || null,
        eu_rep_state: formData.euRepState || null,
        eu_rep_country: formData.euRepCountry || null,
        task_main_contact_complete: taskComplete.mainContact,
        task_dpo_complete: taskComplete.dpo,
        task_eu_rep_complete: taskComplete.euRep,
        task_privacy_policy_complete: taskComplete.privacyPolicy,
        task_nda_complete: taskComplete.nda,
        task_contract_clauses_complete: taskComplete.contractClauses,
      };

      // Fetch existing record to use as snapshot
      const existingResult = await gdprService.getByCompanyId(companyId);
      const existingRecord = existingResult.data;
      
      const result = await gdprService.upsert(
        gdprData,
        existingRecord,
        { companyId, userId: user.id }
      );

      if (result.error) throw result.error;
      
      if (result.data && !gdprId) {
        setGdprId(result.data.id);
      }

      toast({
        title: 'Saved',
        description: 'GDPR information has been saved successfully.',
      });
    } catch (error: any) {
      console.error('Error saving GDPR data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save GDPR data.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="stat-card">
        <p className="text-muted-foreground">
          You must be associated with a company to manage GDPR settings. Please update your profile with a company first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="stat-card">
        <p className="text-muted-foreground">
          If you collect data from EU citizens (even if you are based outside of the EU) you need to complete the following 5 tasks, with particular importance on the Privacy Policy (3) and Privacy Notices (4) tasks in order to be GDPR compliant. Mark the tasks as complete as you do them.
        </p>
      </div>

      {/* Task 1 - Required GDPR Contact Information */}
      <div className="stat-card">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full border-2 border-muted-foreground flex items-center justify-center text-xs">
            {taskComplete.mainContact && taskComplete.dpo && taskComplete.euRep ? '✓' : ''}
          </span>
          1 - Required GDPR Contact Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Main Contact */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Who is the main contact for GDPR notifications?
            </label>
            <Input
              placeholder="Search"
              value={formData.mainContact}
              onChange={(e) => handleInputChange('mainContact', e.target.value)}
              className="bg-secondary"
            />
            <div className="flex items-center gap-2 mt-3">
              <Checkbox
                id="mainContactComplete"
                checked={taskComplete.mainContact}
                onCheckedChange={() => handleTaskToggle('mainContact')}
              />
              <label htmlFor="mainContactComplete" className="text-sm text-muted-foreground cursor-pointer">
                Mark above task as complete
              </label>
            </div>
          </div>

          {/* DPO */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Who is your Data Protection Officer? (optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Name"
                value={formData.dpoName}
                onChange={(e) => handleInputChange('dpoName', e.target.value)}
                className="bg-secondary"
              />
              <Input
                placeholder="Email"
                value={formData.dpoEmail}
                onChange={(e) => handleInputChange('dpoEmail', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Checkbox
                id="dpoComplete"
                checked={taskComplete.dpo}
                onCheckedChange={() => handleTaskToggle('dpo')}
              />
              <label htmlFor="dpoComplete" className="text-sm text-muted-foreground cursor-pointer">
                Mark above task as complete
              </label>
            </div>
          </div>
        </div>

        {/* EU Representative */}
        <div className="border-t border-border pt-6">
          <label className="text-sm font-medium text-foreground mb-4 block">
            If outside the EU, who is your EU representative?
          </label>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Name</label>
              <Input
                value={formData.euRepName}
                onChange={(e) => handleInputChange('euRepName', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Email</label>
              <Input
                value={formData.euRepEmail}
                onChange={(e) => handleInputChange('euRepEmail', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Phone</label>
              <Input
                value={formData.euRepPhone}
                onChange={(e) => handleInputChange('euRepPhone', e.target.value)}
                className="bg-secondary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Address Line 1</label>
              <Input
                value={formData.euRepAddress1}
                onChange={(e) => handleInputChange('euRepAddress1', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Address Line 2</label>
              <Input
                value={formData.euRepAddress2}
                onChange={(e) => handleInputChange('euRepAddress2', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Address Line 3</label>
              <Input
                value={formData.euRepAddress3}
                onChange={(e) => handleInputChange('euRepAddress3', e.target.value)}
                className="bg-secondary"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">City</label>
              <Input
                value={formData.euRepCity}
                onChange={(e) => handleInputChange('euRepCity', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">State / Province</label>
              <Input
                value={formData.euRepState}
                onChange={(e) => handleInputChange('euRepState', e.target.value)}
                className="bg-secondary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Country</label>
              <Select value={formData.euRepCountry} onValueChange={(value) => handleInputChange('euRepCountry', value)}>
                <SelectTrigger className="bg-secondary">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="austria">Austria</SelectItem>
                  <SelectItem value="belgium">Belgium</SelectItem>
                  <SelectItem value="france">France</SelectItem>
                  <SelectItem value="germany">Germany</SelectItem>
                  <SelectItem value="ireland">Ireland</SelectItem>
                  <SelectItem value="italy">Italy</SelectItem>
                  <SelectItem value="netherlands">Netherlands</SelectItem>
                  <SelectItem value="spain">Spain</SelectItem>
                  <SelectItem value="other">Other EU Country</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="euRepComplete"
              checked={taskComplete.euRep}
              onCheckedChange={() => handleTaskToggle('euRep')}
            />
            <label htmlFor="euRepComplete" className="text-sm text-muted-foreground cursor-pointer">
              Mark above task as complete
            </label>
          </div>
        </div>
      </div>

      {/* Task 2 - Update Privacy Policy */}
      <div className="stat-card">
        <h3 className="text-lg font-semibold text-foreground mb-4">2 - Update your own Privacy Policy</h3>
        <p className="text-muted-foreground mb-4">
          Since you're using Prolarus to capture and store vendor invoicing and pricing data, you need to update your own Privacy Policy to disclose this. An example of what needs to be added is included below.
        </p>
        <div className="bg-secondary rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-foreground mb-2">Example text:</p>
          <p className="text-sm text-muted-foreground italic">
            "We use your information to help better understand the accuracy of the invoice that you send us. This information is only used to process the invoice that you send us using the Prolarus compliance product."
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="privacyPolicyComplete"
            checked={taskComplete.privacyPolicy}
            onCheckedChange={() => handleTaskToggle('privacyPolicy')}
          />
          <label htmlFor="privacyPolicyComplete" className="text-sm text-muted-foreground cursor-pointer">
            Mark above task as complete
          </label>
        </div>
      </div>

      {/* Task 3 - Request an NDA */}
      <div className="stat-card">
        <h3 className="text-lg font-semibold text-foreground mb-4">3 - Request an NDA</h3>
        <p className="text-muted-foreground mb-4">
          A Non-Disclosure Agreement (NDA) is available between your company and Prolarus if you'd like one.
        </p>
        <p className="text-muted-foreground mb-4">
          Email <a href="mailto:privacy@prolarus.com" className="text-primary hover:underline">privacy@prolarus.com</a> to request an NDA.
        </p>
        <div className="flex items-center gap-2">
          <Checkbox
            id="ndaComplete"
            checked={taskComplete.nda}
            onCheckedChange={() => handleTaskToggle('nda')}
          />
          <label htmlFor="ndaComplete" className="text-sm text-muted-foreground cursor-pointer">
            Mark above task as complete
          </label>
        </div>
      </div>

      {/* Task 4 - Request Standard Contract Clauses */}
      <div className="stat-card">
        <h3 className="text-lg font-semibold text-foreground mb-4">4 - Request Standard Contract Clauses</h3>
        <p className="text-muted-foreground mb-4">
          Standard Contract Clauses govern the transfer of data outside of the EU. If you need Standard Contract Clauses please contact <a href="mailto:privacy@prolarus.com" className="text-primary hover:underline">privacy@prolarus.com</a> to request them.
        </p>
        <div className="flex items-center gap-2">
          <Checkbox
            id="contractClausesComplete"
            checked={taskComplete.contractClauses}
            onCheckedChange={() => handleTaskToggle('contractClauses')}
          />
          <label htmlFor="contractClausesComplete" className="text-sm text-muted-foreground cursor-pointer">
            Mark above task as complete
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isLoading} className="gap-2">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
